import {
    parseDate,
    type DateRangeValue,
} from "../../../../../../components/general/DateRangeInput";
import { DEFAULT_LOCALE } from "../../../../../../types/constants.ts";
import {
    InvoiceDateFieldEnum,
    type InvoiceFilterAccessors,
    type InvoiceFilterState,
} from "../types/invoiceFilter.ts";

export const FILTERED_EMPTY_MESSAGE = "Geen facturen die aan de filters voldoen.";

const ISO_DATE_LENGTH = 10;
const NO_RELATIONS_SELECTED = 0;
const RELATION_SORT_OPTIONS: Intl.CollatorOptions = { sensitivity: "base" };
const EMPTY_RELATION = "";

const EMPTY_DATE_RANGE: DateRangeValue = { from: null, to: null };

export function createEmptyInvoiceFilterState(
    dateField: InvoiceDateFieldEnum
): InvoiceFilterState {
    return {
        dateRange: { ...EMPTY_DATE_RANGE },
        dateField,
        relations: [],
    };
}

export function isInvoiceFilterActive(filters: InvoiceFilterState): boolean {
    return (
        filters.dateRange.from !== null ||
        filters.dateRange.to !== null ||
        filters.relations.length > NO_RELATIONS_SELECTED
    );
}

function normalizeRelation(relation: string | null | undefined): string {
    return (relation ?? EMPTY_RELATION).trim();
}

function toStartOfDayMs(value: Date | string | null): number | null {
    if (value === null) return null;

    const date =
        typeof value === "string" ? parseDate(value.slice(0, ISO_DATE_LENGTH)) : value;
    if (date === null || Number.isNaN(date.getTime())) return null;

    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function isWithinDateRange(
    isoDate: string | null,
    range: DateRangeValue
): boolean {
    const from = toStartOfDayMs(range.from);
    const to = toStartOfDayMs(range.to);

    if (from === null && to === null) return true;

    const value = toStartOfDayMs(isoDate);
    if (value === null) return false;

    if (from !== null && value < from) return false;
    if (to !== null && value > to) return false;

    return true;
}

export function matchesRelations(
    relation: string | null,
    selectedRelations: string[]
): boolean {
    if (selectedRelations.length === NO_RELATIONS_SELECTED) return true;
    return selectedRelations.includes(normalizeRelation(relation));
}

export function filterInvoiceRows<TRow>(
    rows: TRow[],
    filters: InvoiceFilterState,
    accessors: InvoiceFilterAccessors<TRow>
): TRow[] {
    if (!isInvoiceFilterActive(filters)) return rows;

    return rows.filter(
        (row) =>
            matchesRelations(accessors.getRelation(row), filters.relations) &&
            isWithinDateRange(accessors.getDate(row, filters.dateField), filters.dateRange)
    );
}

export function collectRelationOptions<TRow>(
    rows: TRow[],
    getRelation: (row: TRow) => string | null
): string[] {
    const uniqueRelations = new Set<string>();

    for (const row of rows) {
        const relation = normalizeRelation(getRelation(row));
        if (relation !== EMPTY_RELATION) uniqueRelations.add(relation);
    }

    return Array.from(uniqueRelations).sort((a, b) =>
        a.localeCompare(b, DEFAULT_LOCALE, RELATION_SORT_OPTIONS)
    );
}
